import { api, ApiError, API_BASE } from "./client";
import type {
  MailBackupUsage,
  MailPurgePreview,
  MailPurgeResult,
  MailBatchResult,
  MailboxStatus,
  MailDetail,
  MailFolder,
  MailMessage,
  MailPage,
  MailSavedSearch,
  MailScheduled,
  MailSearchParams,
  MailSendResult,
  MailThreadPage,
  SystemSentDetail,
  SystemSentItem,
} from "./types";

// 個人信箱的 API(後端 `app/routers/mailbox.py`,規格 §2.5)。**只有本人**:每一支都以登入者篩。
//
// 錯誤一律是 `{"detail": {"code", "message"}}`。畫面靠 `mailErrorCode()` 決定要怎麼反應
// (沒啟用 → 啟用頁、`MAILBOX_AUTH` → 紅條請重新輸入密碼、`MAILBOX_UNREACHABLE` → 唯讀備份),
// 要顯示給人看的一律是 `ApiError.message`(就是後端那句 `message`)。

export const MAILBOX_DISABLED = "MAILBOX_DISABLED";
export const MAILBOX_AUTH = "MAILBOX_AUTH";
export const MAILBOX_UNREACHABLE = "MAILBOX_UNREACHABLE";
export const MAILBOX_BAD_PASSWORD = "MAILBOX_BAD_PASSWORD";

/** 附件總計上限(與後端 `compose.MAX_ATTACHMENTS` 一致)。前端先擋,省得傳了 15 MB 才被 413。 */
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

/** 錯誤的代碼;不是後端的結構化錯誤(網路斷了、別的形狀)回 `null`。 */
export function mailErrorCode(err: unknown): string | null {
  return err instanceof ApiError ? err.code : null;
}

/** 錯誤要顯示的那一句。 */
export function mailErrorMessage(err: unknown, fallback = "沒有成功,請稍後再試"): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

/**
 * 這封信的檔案(附件、原始碼、.eml)在哪一組端點:一般的信是 `/mailbox/messages/{id}`,
 * 「系統寄出」是 `/mailbox/system-sent/{id}`(同樣的子路徑、同樣的回應)。
 */
export type MailScope = "messages" | "system-sent";

/** 附件的下載網址(`<a href>` 直接用;走同一個 session cookie)。 */
export function attachmentUrl(messageId: number, part: number, inline = false, scope: MailScope = "messages"): string {
  return `${API_BASE}/mailbox/${scope}/${messageId}/attachments/${part}${inline ? "?inline=1" : ""}`;
}

/** 全部附件打包成 zip。 */
export function attachmentsZipUrl(messageId: number, scope: MailScope = "messages"): string {
  return `${API_BASE}/mailbox/${scope}/${messageId}/attachments.zip`;
}

/** 備份的原始檔(.eml),一個位元組都不改。 */
export function emlUrl(messageId: number, scope: MailScope = "messages"): string {
  return `${API_BASE}/mailbox/${scope}/${messageId}/download.eml`;
}

/** 信裡的行事曆邀請(.ics)。 */
export function calendarUrl(messageId: number): string {
  return `${API_BASE}/mailbox/messages/${messageId}/calendar.ics`;
}

/**
 * 後端願意 `?inline=1` 預覽的型別(`render.PREVIEW_TYPES` 與它認的別名)。**SVG / HTML 永遠不在這裡**;
 * 不在清單裡的一律只給下載,前端連預覽鈕都不畫。
 */
const PREVIEW_TYPES: Record<string, "pdf" | "image"> = {
  "application/pdf": "pdf",
  "image/png": "image",
  "image/jpeg": "image",
  "image/jpg": "image",
  "image/pjpeg": "image",
  "image/gif": "image",
  "image/webp": "image",
};

/** 這個附件能不能在頁面上預覽、用哪一種。 */
export function previewKind(contentType: string): "pdf" | "image" | null {
  return PREVIEW_TYPES[(contentType || "").trim().toLowerCase()] ?? null;
}

export interface MessageQuery extends MailSearchParams {
  folder: string;
  before?: number | null;
}

/**
 * 「系統備份」虛擬資料夾:列出全部備份的信(含雲端已刪除),唯讀。
 * 後端保留字(`*` 在 IMAP 是萬用字元,不會是真資料夾名)。一般資料夾與「全部資料夾」都不含雲端已刪除的信。
 */
export const BACKUP_FOLDER = "*backup";
export const BACKUP_LABEL = "系統備份";

/**
 * 「系統寄出」虛擬資料夾:系統替本人寄的信(報價單、回簽信)與有沒有寄成功,唯讀,只有本人。
 * 同樣是保留字(`*`)。網址 `/mailbox?folder=*sent-by-system` 直接開在這裡(舊的 `/outbox` 轉過來)。
 */
export const SYSTEM_SENT_FOLDER = "*sent-by-system";
export const SYSTEM_SENT_LABEL = "系統寄出";
export const SYSTEM_SENT_URL = `/mailbox?folder=${SYSTEM_SENT_FOLDER}`;

/** 進階搜尋條件 → 查詢字串:沒設的不送。 */
function searchQuery(params: MessageQuery) {
  return {
    folder: params.folder,
    before: params.before ?? undefined,
    q: params.q || undefined,
    date_from: params.date_from || undefined,
    date_to: params.date_to || undefined,
    has_attachments: params.has_attachments ?? undefined,
    attachment: params.attachment || undefined,
    from: params.from || undefined,
    to: params.to || undefined,
    flagged: params.flagged ?? undefined,
    unread: params.unread ?? undefined,
  };
}

export type BatchAction = "seen" | "unseen" | "flag" | "unflag" | "trash" | "move";

/** 封存(快捷鍵 `e`)。 */
export const ARCHIVE_FOLDER = "INBOX.Archive";

/** 動作的回應:`sync_pending` 表示雲端已經改好、備份還沒同步回來(背景很快會補上)。 */
export type MailActionResult = MailMessage & { sync_pending?: boolean };

export interface SendInput {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  replyToId?: number | null;
  forwardId?: number | null;
  files: File[];
}

export interface SendOptions {
  /** 自動儲存的草稿;寄出 / 排好之後後端把它從草稿匣刪掉。 */
  draftMessageId?: string | null;
}

/** `withFiles: false`(草稿自動儲存):不帶附件 —— 草稿不附檔,附件只在寄出時帶上(規格 §2.3)。 */
function composeForm(input: SendInput, withFiles = true): FormData {
  const form = new FormData();
  form.append("to", input.to);
  form.append("cc", input.cc);
  form.append("bcc", input.bcc);
  form.append("subject", input.subject);
  form.append("body", input.body);
  if (input.replyToId != null) form.append("reply_to_id", String(input.replyToId));
  if (input.forwardId != null) form.append("forward_id", String(input.forwardId));
  if (withFiles) for (const file of input.files) form.append("files", file, file.name);
  return form;
}

/** `/send` 帶了 `send_at` 的回應。 */
export interface ScheduleResult {
  scheduled_id: number;
  send_at: string;
}

export const mailbox = {
  status: () => api.get<MailboxStatus>("/mailbox/status"),
  enable: (password: string) => api.post<MailboxStatus>("/mailbox/enable", { password }),
  disable: () => api.del<void>("/mailbox"),
  folders: () => api.get<MailFolder[]>("/mailbox/folders"),
  messages: (query: MessageQuery) => api.get<MailPage>("/mailbox/messages", searchQuery(query)),
  threads: (folder: string, before: number | null = null) =>
    api.get<MailThreadPage>("/mailbox/threads", { folder, before: before ?? undefined }),
  /** 一串的全部信。`key` 放查詢參數:它是 Message-ID,可能含 `/`、`..`,放路徑會被 nginx 正規化掉。 */
  thread: (key: string) => api.get<{ thread_key: string; items: MailMessage[] }>("/mailbox/thread", { key }),
  message: (id: number, images = false) =>
    api.get<MailDetail>(`/mailbox/messages/${id}`, images ? { images: 1 } : undefined),
  flags: (id: number, change: { seen?: boolean; flagged?: boolean }) =>
    api.post<MailActionResult>(`/mailbox/messages/${id}/flags`, change),
  trash: (id: number) => api.post<MailActionResult>(`/mailbox/messages/${id}/trash`),
  sync: (folder: string) => api.post<MailboxStatus & { sync_pending: boolean }>("/mailbox/sync", undefined, { folder }),
  unread: () => api.get<{ inbox_unread: number }>("/mailbox/unread"),
  signature: () => api.get<{ signature: string }>("/mailbox/signature"),
  saveSignature: (signature: string) => api.put<{ signature: string }>("/mailbox/signature", { signature }),
  send: (input: SendInput, options: SendOptions = {}) => {
    const form = composeForm(input);
    if (options.draftMessageId) form.append("draft_message_id", options.draftMessageId);
    return api.upload<MailSendResult>("/mailbox/send", form);
  },
  /** 排程寄送:同一支 `/send`,多帶 `send_at`(ISO,帶時區)。 */
  schedule: (input: SendInput, sendAt: string, options: SendOptions = {}) => {
    const form = composeForm(input);
    form.append("send_at", sendAt);
    if (options.draftMessageId) form.append("draft_message_id", options.draftMessageId);
    return api.upload<ScheduleResult>("/mailbox/send", form);
  },
  batch: (ids: number[], action: BatchAction, folder?: string) =>
    api.post<MailBatchResult>("/mailbox/batch", folder ? { ids, action, folder } : { ids, action }),
  move: (id: number, folder: string) => api.post<MailActionResult>(`/mailbox/messages/${id}/move`, { folder }),
  markAllRead: (folder: string) =>
    api.post<{ marked: number; sync_pending: boolean }>(
      `/mailbox/folders/${encodeURIComponent(folder)}/mark-all-read`,
    ),
  backupUsage: () => api.get<MailBackupUsage>("/mailbox/backup/usage"),
  purgePreview: (range: { date_from?: string; date_to?: string }) =>
    api.post<MailPurgePreview>("/mailbox/backup/purge/preview", range),
  purge: (range: { date_from?: string; date_to?: string }) =>
    api.post<MailPurgeResult>("/mailbox/backup/purge", { ...range, confirm: "刪除" }),
  savedSearches: () => api.get<MailSavedSearch[]>("/mailbox/saved-searches"),
  saveSearch: (name: string, params: MailSearchParams) =>
    api.post<MailSavedSearch>("/mailbox/saved-searches", { name, params }),
  deleteSavedSearch: (id: number) => api.del<void>(`/mailbox/saved-searches/${id}`),
  setFollowup: (id: number, days: 1 | 3 | 7) =>
    api.post<{ id: number; message_id: number; due_at: string }>(`/mailbox/messages/${id}/followup`, { days }),
  clearFollowup: (id: number) => api.del<void>(`/mailbox/messages/${id}/followup`),
  /** 存一版草稿(有上一版就帶 `draftId`,後端存新的、刪舊的)。**不帶附件**(附件只在寄出時帶上)。 */
  saveDraft: (input: SendInput, draftId: string | null) => {
    const form = composeForm(input, false);
    if (draftId) form.append("draft_id", draftId);
    return api.uploadPut<{ draft_message_id: string }>("/mailbox/drafts", form);
  },
  discardDraft: (draftId: string) => api.del<void>(`/mailbox/drafts/${encodeURIComponent(draftId)}`),
  scheduled: () => api.get<MailScheduled[]>("/mailbox/scheduled"),
  reschedule: (id: number, sendAt: string) =>
    api.patch<MailScheduled>(`/mailbox/scheduled/${id}`, { send_at: sendAt }),
  cancelScheduled: (id: number) => api.del<void>(`/mailbox/scheduled/${id}`),
  /** 完整標頭與原文(純文字,後端回 text/plain)。`scope` 見 {@link MailScope}。 */
  source: (id: number, scope: MailScope = "messages") => api.get<string>(`/mailbox/${scope}/${id}/source`),
  /** 「系統寄出」:本人的列表與讀一筆。不需要啟用信箱。 */
  systemSent: (before: number | null = null) =>
    api.get<{ items: SystemSentItem[]; next_before: number | null }>("/mailbox/system-sent", {
      before: before ?? undefined,
    }),
  systemSentOne: (id: number) => api.get<SystemSentDetail>(`/mailbox/system-sent/${id}`),
};

/** 信箱的東西變了(讀了一封、刪了一封)。側邊欄的未讀數聽到就重問。 */
export const MAILBOX_CHANGED_EVENT = "nq-mailbox-changed";

export function announceMailboxChanged(): void {
  window.dispatchEvent(new Event(MAILBOX_CHANGED_EVENT));
}
